(() => {
  const KEYS = ["work", "conference", "meeting", "focus", "entrance", "reception", "relax", "storage"];
  const CARD_SELECTOR = '.collection-list__item[data-office-legend]';
  const TEXT_LIMIT = 180;
  const cleanText = (value, limit = TEXT_LIMIT) => typeof value === 'string' ? value.trim().slice(0, limit) : '';

  function permittedImage(value) {
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && url.hostname === 'cdn.shopify.com' && !url.port && !url.username && !url.password && /^\/s\/files\//.test(url.pathname) && !url.hash ? url.href : null;
    } catch (_) { return null; }
  }

  function permittedProduct(value, origin) {
    if (typeof value !== 'string' || /[\u0000-\u0020\\]/.test(value)) return null;
    try {
      const url = new URL(value, origin);
      const variants = url.searchParams.getAll('variant');
      const match = url.pathname.match(/^\/products\/([^/]+)\/?$/);
      const handle = match ? decodeURIComponent(match[1]) : '';
      return url.origin === origin && !url.username && !url.password && !url.hash && /^[\p{L}\p{N}\p{M}][\p{L}\p{N}\p{M}_-]*$/u.test(handle) && variants.length === 1 && /^[1-9]\d*$/.test(variants[0]) && [...url.searchParams.keys()].every((key) => key === 'variant') ? `/products/${encodeURIComponent(handle)}?variant=${variants[0]}` : null;
    } catch (_) { return null; }
  }

  function permittedBrowse(value, currentUrl) {
    try {
      const current = new URL(currentUrl);
      const url = new URL(value, current);
      return typeof value === 'string' && value.startsWith('#') && url.origin === current.origin && url.pathname === current.pathname && url.search === current.search && /^#[a-z0-9_-]+$/i.test(url.hash) ? url.hash : null;
    } catch (_) { return null; }
  }

  function permittedCategory(value, origin, nodeId) {
    if (typeof value !== 'string' || /[\u0000-\u0020\\]/.test(value)) return null;
    try {
      const url = new URL(value, origin);
      const match = url.pathname.match(/^\/collections\/odp-202601-([1-9]\d*)$/);
      return url.origin === origin && !url.username && !url.password && !url.search && !url.hash && match && (!nodeId || nodeId === `202601:${match[1]}`) ? url.pathname : null;
    } catch (_) { return null; }
  }

  function resolveDestination(item, origin, resolver) {
    if (typeof resolver?.resolve === 'function') {
      try {
        const result = resolver.resolve(item, { origin });
        const href = result?.kind === 'product' ? permittedProduct(result.href, origin) : result?.kind === 'category' ? permittedCategory(result.href, origin) : null;
        return href ? { href, kind: result.kind } : { href: null, kind: 'unavailable' };
      } catch (_) {}
    } else if (item.canLink === true && (item.status === undefined || item.status === 'ACTIVE')) {
      const variant = String(item.variantId ?? '').replace(/^gid:\/\/shopify\/ProductVariant\//, '');
      const exactVariant = /^[1-9]\d*$/.test(variant) && (typeof item.variantId !== 'number' || Number.isSafeInteger(item.variantId));
      const handle = typeof item.handle === 'string' && /^[\p{L}\p{N}\p{M}][\p{L}\p{N}\p{M}_-]*$/u.test(item.handle) ? item.handle : '';
      const value = item.handle ? (handle && exactVariant ? `/products/${encodeURIComponent(handle)}?variant=${variant}` : null) : item.productUrl;
      let href = permittedProduct(value, origin);
      if (href && item.variantId != null && (!exactVariant || new URL(href, origin).searchParams.get('variant') !== variant)) href = null;
      if (href) return { href, kind: 'product' };
    }
    // Static routes come only from the verified manifest and must name the same exact ODP node.
    const href = /^202601:[1-9]\d*$/.test(item.categoryNodeId || '') ? permittedCategory(item.categoryUrl, origin, item.categoryNodeId) : null;
    return href ? { href, kind: 'category' } : { href: null, kind: 'unavailable' };
  }

  function normalizeGroup(group, origin, resolver) {
    const hashes = new Set();
    const items = [];
    for (const raw of Array.isArray(group?.items) ? group.items : []) {
      if (!raw || typeof raw !== 'object') continue;
      const imageUrl = permittedImage(raw.imageUrl);
      const imageHash = cleanText(raw.imageHash, 128).toLowerCase();
      const id = cleanText(String(raw.id ?? raw.productId ?? raw.sku ?? ''), 100);
      const sku = cleanText(raw.sku, 100);
      const title = cleanText(raw.title) || sku;
      if (!imageUrl || !/^[a-f0-9]{32,128}$/.test(imageHash) || !title || !id || !sku || hashes.has(imageHash)) continue;
      hashes.add(imageHash);
      const destinationItem = { sku, productId: raw.productId, variantId: raw.variantId, handle: raw.handle, status: raw.status, canLink: raw.canLink === true, productUrl: raw.productUrl, categoryNodeId: raw.categoryNodeId, categoryUrl: raw.categoryUrl };
      const destination = resolveDestination(destinationItem, origin, resolver);
      items.push({ id, sku, title, imageUrl, imageHash, inquiryOnly: raw.inquiryOnly === true, destinationItem, destination, productUrl: destination.kind === 'product' ? destination.href : null, canLink: destination.kind === 'product' });
      if (items.length === 30) break;
    }
    return { items, imageCount: items.length, readyCount: items.filter((item) => item.canLink).length };
  }

  function ellipsePoints(count, rx, ry, phase = 0) {
    const samples = 1440;
    const lengths = [0];
    let total = 0;
    for (let i = 1; i <= samples; i += 1) {
      const angle = (i - .5) * Math.PI * 2 / samples;
      total += Math.hypot(rx * Math.sin(angle), ry * Math.cos(angle)) * Math.PI * 2 / samples;
      lengths.push(total);
    }
    return Array.from({ length: count }, (_, index) => {
      const target = ((index + phase) / count % 1) * total;
      let low = 0;
      let high = samples;
      while (low + 1 < high) {
        const middle = (low + high) >> 1;
        if (lengths[middle] < target) low = middle;
        else high = middle;
      }
      const fraction = (target - lengths[low]) / (lengths[high] - lengths[low] || 1);
      const angle = (low + fraction) * Math.PI * 2 / samples;
      return { x: rx * Math.cos(angle), y: ry * Math.sin(angle), rotation: Math.sin(angle * 2) * 5 };
    });
  }

  function ringGeometry(count, width, height, center = { width: 230, height: 144 }) {
    if (!count || count > 30 || width < 1000 || height < 500) return null;
    const ratio = 1.15;
    const patterns = [];
    const rx = width / 2 - 85;
    if (count <= 12) patterns.push(ellipsePoints(count, rx * .82, (height / 2 - 80) * .82, .5));
    else {
      for (const innerCount of new Set([Math.round(count / 3), Math.round(count * .4)])) {
        for (const margin of [66, 78]) {
          for (const factor of [.55, .6]) {
            for (const phase of [0, .25]) {
              const ry = height / 2 - margin;
              patterns.push([...ellipsePoints(innerCount, rx * factor, ry * factor, phase), ...ellipsePoints(count - innerCount, rx, ry, .5)]);
            }
          }
        }
      }
    }
    let bestSize = 0;
    let bestPoints;
    for (const points of patterns) {
      const fits = (size) => {
        const boxes = points.map((point) => {
          const angle = Math.abs(point.rotation) * Math.PI / 180;
          return { x: point.x, y: point.y, width: size * Math.cos(angle) + size / ratio * Math.sin(angle) + 14, height: size / ratio * Math.cos(angle) + size * Math.sin(angle) + 14 };
        });
        for (let i = 0; i < boxes.length; i += 1) {
          const box = boxes[i];
          if (Math.abs(box.x) + box.width / 2 > width / 2 - 9 || Math.abs(box.y) + box.height / 2 > height / 2 - 9) return false;
          if (Math.abs(box.x) < (box.width + center.width) / 2 + 10 && Math.abs(box.y) < (box.height + center.height) / 2 + 10) return false;
          for (let j = 0; j < i; j += 1) {
            const other = boxes[j];
            if (Math.abs(box.x - other.x) < (box.width + other.width) / 2 && Math.abs(box.y - other.y) < (box.height + other.height) / 2) return false;
          }
        }
        return true;
      };
      let low = 0;
      let high = count <= 12 ? 216 : 200;
      for (let i = 0; i < 16; i += 1) {
        const middle = (low + high) / 2;
        if (fits(middle)) low = middle;
        else high = middle;
      }
      if (low > bestSize) { bestSize = low; bestPoints = points; }
    }
    if (bestSize < 76) return null;
    const size = Math.floor(bestSize);
    return bestPoints.map((point) => ({ ...point, dx: point.x, dy: point.y, x: point.x + width / 2, y: point.y + height / 2, width: size, height: size / ratio }));
  }

  function outerEnvelope(rectangles, padding = 10) {
    const points = rectangles.filter((r) => r && [r.left, r.top, r.right, r.bottom].every(Number.isFinite) && r.right > r.left && r.bottom > r.top).flatMap((r) => [
      { x: r.left - padding, y: r.top - padding }, { x: r.right + padding, y: r.top - padding },
      { x: r.right + padding, y: r.bottom + padding }, { x: r.left - padding, y: r.bottom + padding }
    ]).sort((a, b) => a.x - b.x || a.y - b.y);
    const cross = (a, b, c) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
    const half = (values) => {
      const result = [];
      for (const point of values) {
        while (result.length > 1 && cross(result.at(-2), result.at(-1), point) <= 0) result.pop();
        result.push(point);
      }
      result.pop();
      return result;
    };
    return points.length ? [...half(points), ...half([...points].reverse())] : [];
  }

  function insideEnvelope(point, hull) {
    if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y) || hull.length < 3) return false;
    let sign = 0;
    for (let i = 0; i < hull.length; i += 1) {
      const a = hull[i], b = hull[(i + 1) % hull.length];
      const cross = (b.x - a.x) * (point.y - a.y) - (b.y - a.y) * (point.x - a.x);
      if (Math.abs(cross) < .01) continue;
      if (sign && Math.sign(cross) !== sign) return false;
      sign = Math.sign(cross);
    }
    return true;
  }

  const api = { outerEnvelope, insideEnvelope, normalizeGroup, resolveDestination, permittedCategory, permittedImage, permittedProduct, permittedBrowse, ellipsePoints, ringGeometry };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window === 'undefined' || !window.customElements || window.customElements.get('ace-category-flower')) return;

  class AceCategoryFlower extends HTMLElement {
    async connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.dialog = this.querySelector('dialog');
      this.scope = this.closest('[data-office-explorer]');
      if (!this.scope || typeof this.dialog?.showModal !== 'function') return;
      this.stage = this.querySelector('[data-flower-stage]');
      this.center = this.querySelector('[data-flower-center]');
      this.petals = this.querySelector('[data-flower-petals]');
      this.closeButton = this.querySelector('[data-flower-close]');
      this.browse = this.querySelector('[data-flower-browse]');
      this.coarseMedia = window.matchMedia('(pointer: coarse)');
      this.cards = new Map();
      this.groups = new Map();
      try {
        this.categories = JSON.parse(this.querySelector('[data-flower-categories]').textContent);
        const manifestUrl = new URL(this.dataset.manifestUrl, location.href);
        if (manifestUrl.protocol !== 'https:' || (manifestUrl.origin !== location.origin && manifestUrl.hostname !== 'cdn.shopify.com')) return;
        const response = await fetch(manifestUrl.href, { signal, credentials: 'same-origin' });
        if (!response.ok) return;
        const manifest = await response.json();
        if (signal.aborted || !this.isConnected) return;
        for (const key of KEYS) {
          const group = normalizeGroup(manifest.groups?.[key], location.origin, window.AceProductDestination);
          const category = this.categories[key];
          if (!group.imageCount || !cleanText(category?.title)) continue;
          this.groups.set(key, { ...group, title: cleanText(category.title) });
        }
      } catch (_) { return; }
      if (!this.groups.size) return;
      const options = { signal };
      const destinationsReady = () => {
        this.refreshDestinations();
        const resolver = window.AceProductDestination;
        if (resolver) {
          Promise.resolve(resolver.ready).then(() => { if (!signal.aborted && this.isConnected) this.refreshDestinations(); }, () => {});
        }
      };
      document.addEventListener('ace:product-destination-ready', destinationsReady, options);
      document.addEventListener('ace:board-ready', destinationsReady, options);
      destinationsReady();
      this.scope.querySelectorAll(CARD_SELECTOR).forEach((card) => {
        const key = card.dataset.officeLegend;
        if (!this.groups.has(key)) return;
        const links = Array.from(card.querySelectorAll('a')).filter((link) => !link.hasAttribute('data-flower-ignore'));
        const trigger = links.find((link) => link.getAttribute('tabindex') !== '-1') || links[0];
        const browseUrl = permittedBrowse(trigger?.getAttribute('href'), location.href);
        if (!trigger || !browseUrl) return;
        this.groups.get(key).browseUrl = browseUrl;
        this.cards.set(key, { card, trigger, links });
        links.forEach((link) => {
          link.setAttribute('aria-haspopup', 'dialog');
          link.setAttribute('aria-controls', this.id + '-dialog');
          link.setAttribute('aria-expanded', 'false');
        });
        const hover = (event) => {
          if (event.pointerType === 'touch' || event.buttons > 0 || Date.now() < (this.touchCooldown || 0)) return;
          this.scheduleHover(key, trigger);
        };
        card.addEventListener('pointerenter', hover, options);
        card.addEventListener('mouseenter', hover, options);
        card.addEventListener('pointerleave', () => this.cancelHover(), options);
        card.addEventListener('mouseleave', () => this.cancelHover(), options);
        card.addEventListener('pointerdown', (event) => {
          this.cancelHover();
          if (event.pointerType === 'touch') this.touchCooldown = Date.now() + 800;
        }, options);
      });
      this.dialog.id = this.id + '-dialog';
      this.scope.addEventListener('click', (event) => this.activateCard(event), { signal, capture: true });
      this.scope.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') this.activateCard(event);
      }, { signal, capture: true });
      this.closeButton.addEventListener('click', () => this.requestClose(), options);
      this.browse.addEventListener('click', (event) => {
        if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        const trigger = this.cards.get(this.activeKey)?.trigger;
        if (!trigger) return;
        event.preventDefault();
        event.stopPropagation();
        this.afterClose = () => { this.bypassTrigger = trigger; trigger.click(); this.bypassTrigger = null; };
        this.requestClose();
      }, options);
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.requestClose(); }, options);
      this.dialog.addEventListener('close', () => {
        if (!this.internalClose && this.activeKey) this.requestClose();
      }, options);
      this.dialog.addEventListener('pointermove', (event) => this.trackBoundary(event), options);
      this.dialog.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'touch' && this.boundaryArmed) this.scheduleBoundaryClose(true);
      }, options);
      this.dialog.addEventListener('keydown', () => { this.cancelBoundaryClose(); this.boundaryArmed = false; }, options);
      this.querySelector('[data-flower-viewport]').addEventListener('scroll', () => this.cancelBoundaryClose(), options);
      this.dialog.addEventListener('pointerdown', (event) => {
        this.cancelBoundaryClose();
        this.emptyPointerDown = this.isEmptyBackground(event.target);
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (this.emptyPointerDown && this.isEmptyBackground(event.target) && !event.defaultPrevented) this.requestClose();
        this.emptyPointerDown = false;
        const link = event.target.closest('a[href]');
        if (link && this.dialog.contains(link) && !event.defaultPrevented && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0) {
          this.prepareNavigation();
        }
      }, options);
      // Opening is a visual overlay only. Native navigation keeps its own history.
      window.addEventListener('popstate', () => this.closeUI(false, false), options);
      window.addEventListener('pageshow', () => this.closeUI(false, false), options);
      window.addEventListener('pagehide', () => this.closeUI(false, false), options);
      window.addEventListener('blur', () => this.cancelHover(), options);
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.cancelHover(); }, options);
      this.resizeObserver = new ResizeObserver(() => this.queueLayout());
      this.resizeObserver.observe(this.dialog);
      this.coarseMedia.addEventListener('change', () => this.queueLayout(), options);
      // Also handle a pointer already resting on a card when its data finishes loading.
      this.cards.forEach(({ card, trigger }, key) => {
        if (card.matches(':hover')) this.scheduleHover(key, trigger);
      });
    }

    activateCard(event) {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || (event.type === 'click' && event.button !== 0) || (event.type === 'keydown' && event.repeat)) return;
      const link = event.target.closest('a');
      if (link === this.bypassTrigger) return;
      const card = link?.closest(CARD_SELECTOR);
      const key = card?.dataset.officeLegend;
      const entry = this.cards.get(key);
      if (!entry || !entry.links.includes(link) || entry.card !== card || !this.scope.contains(card) || this.dialog.open) return;
      this.cancelHover();
      if (this.open(key, link)) event.preventDefault();
    }

    cancelHover() { clearTimeout(this.hoverTimer); }

    scheduleHover(key, trigger) {
      this.cancelHover();
      if (this.dialog.open || Date.now() < (this.hoverCooldown || 0)) return;
      this.hoverTimer = setTimeout(() => this.open(key, trigger), 100);
    }

    open(key, trigger) {
      const group = this.groups.get(key);
      if (!group?.browseUrl || !this.cards.has(key) || this.dialog.open || document.querySelector('dialog[open]')) return false;
      this.activeKey = key;
      this.boundaryArmed = false;
      this.lastBoundaryPoint = null;
      this.cancelBoundaryClose();
      this.refreshDestinations();
      this.trigger = trigger || this.cards.get(key)?.trigger;
      this.querySelector('[data-flower-title]').textContent = group.title;
      this.querySelector('[data-flower-count]').textContent = this.dataset.countTemplate.replace('__COUNT__', String(group.imageCount));
      this.querySelector('[data-flower-readiness]').textContent = this.dataset.readyTemplate.replace('__COUNT__', String(group.readyCount));
      this.browse.href = group.browseUrl;
      this.renderItems(group.items);
      this.lockScroll();
      try { this.dialog.showModal(); } catch (_) { this.unlockScroll(); this.activeKey = null; return false; }
      window.scrollTo({ left: this.scrollLock.x, top: this.scrollLock.y, behavior: 'instant' });
      this.cards.get(key)?.links.forEach((link) => link.setAttribute('aria-expanded', 'true'));
      this.querySelector('[data-flower-viewport]').scrollTop = 0;
      this.querySelector('[data-flower-title]').focus({ preventScroll: true });
      this.queueLayout();
      return true;
    }

    renderItems(items) {
      const fragment = document.createDocumentFragment();
      items.forEach((item, index) => {
        const li = document.createElement('li');
        li.className = 'ace-category-flower__petal';
        li.style.setProperty('--flower-delay', `${Math.min(index * 8, 160)}ms`);
        const tile = document.createElement('a');
        tile.className = 'ace-category-flower__product';
        tile.dataset.flowerSku = item.sku;
        const img = document.createElement('img');
        img.className = 'ace-category-flower__image';
        img.src = item.imageUrl;
        img.alt = '';
        img.width = 400;
        img.height = 360;
        img.loading = index < 8 ? 'eager' : 'lazy';
        img.decoding = 'async';
        const label = document.createElement('span');
        label.className = 'ace-category-flower__caption';
        label.textContent = item.title;
        tile.title = item.title;
        tile.append(img, label);
        const status = document.createElement('span');
        status.className = 'ace-category-flower__preparing';
        tile.append(status);
        this.updateTileDestination(tile, item);
        li.append(tile);
        fragment.append(li);
      });
      this.petals.replaceChildren(fragment);
    }

    updateTileDestination(tile, item) {
      const { href, kind } = item.destination;
      tile.setAttribute('aria-label', item.title + (item.inquiryOnly ? ' — ' + this.dataset.inquiry : ''));
      tile.dataset.flowerDestination = kind;
      tile.dataset.flowerInquiry = item.inquiryOnly ? 'true' : 'false';
      if (href) { tile.href = href; tile.removeAttribute('aria-disabled'); }
      else { tile.removeAttribute('href'); tile.setAttribute('aria-disabled', 'true'); }
      const status = tile.querySelector('.ace-category-flower__preparing');
      status.hidden = kind === 'product' && !item.inquiryOnly;
      status.textContent = item.inquiryOnly ? this.dataset.inquiry : (kind === 'category' ? this.dataset.categoryFallback : this.dataset.preparing);
    }

    refreshDestinations() {
      this.groups.forEach((group, key) => {
        group.items.forEach((item, index) => {
          item.destination = resolveDestination(item.destinationItem, location.origin, window.AceProductDestination);
          item.canLink = item.destination.kind === 'product';
          item.productUrl = item.canLink ? item.destination.href : null;
          const tile = key === this.activeKey ? this.petals.children[index]?.children[0] : null;
          if (tile) this.updateTileDestination(tile, item);
        });
        group.readyCount = group.items.filter((item) => item.canLink).length;
        if (key === this.activeKey) this.querySelector('[data-flower-readiness]').textContent = this.dataset.readyTemplate.replace('__COUNT__', String(group.readyCount));
      });
    }

    queueLayout() {
      cancelAnimationFrame(this.frame);
      if (this.dialog.open) this.frame = requestAnimationFrame(() => this.layout());
    }

    layout() {
      if (!this.dialog.open) return;
      this.cancelBoundaryClose();
      this.dialog.dataset.layout = 'rings';
      const width = this.stage.clientWidth;
      const height = this.stage.clientHeight;
      this.center.style.setProperty('--flower-center-width', `${Math.min(240, Math.max(196, width * .17))}px`);
      const center = this.center.getBoundingClientRect();
      const points = this.coarseMedia.matches ? null : ringGeometry(this.petals.children.length, width, height, center);
      this.dialog.dataset.layout = points ? 'rings' : 'grid';
      this.ringPoints = points;
      Array.from(this.petals.children).forEach((petal, index) => {
        for (const property of ['x', 'y', 'width', 'height', 'rotation', 'dx', 'dy']) {
          if (points) petal.style.setProperty(`--flower-${property}`, `${points[index][property]}${property === 'rotation' ? 'deg' : 'px'}`);
          else petal.style.removeProperty(`--flower-${property}`);
        }
      });
    }

    boundaryEnvelope() {
      const stage = this.stage.getBoundingClientRect();
      const viewport = this.querySelector('[data-flower-viewport]').getBoundingClientRect();
      const rectangles = [];
      // Final ring geometry prevents the unfolding animation from shrinking the safe area.
      for (const point of this.ringPoints || []) {
        const angle = Math.abs(point.rotation) * Math.PI / 180;
        const width = point.width * Math.cos(angle) + point.height * Math.sin(angle);
        const height = point.height * Math.cos(angle) + point.width * Math.sin(angle);
        rectangles.push({ left: stage.left + point.x - width / 2, right: stage.left + point.x + width / 2, top: stage.top + point.y - height / 2, bottom: stage.top + point.y + height / 2 });
      }
      // Include live transformed bounds, including the enlarged hovered photograph.
      const elements = [this.center, ...Array.from(this.petals.children, (petal) => petal.children[0])];
      for (const element of elements) {
        const rect = element.getBoundingClientRect();
        rectangles.push(this.ringPoints ? rect : { left: Math.max(rect.left, viewport.left), right: Math.min(rect.right, viewport.right), top: Math.max(rect.top, viewport.top), bottom: Math.min(rect.bottom, viewport.bottom) });
      }
      return outerEnvelope(rectangles);
    }

    trackBoundary(event) {
      if (!this.dialog.open || event.pointerType === 'touch' || event.buttons > 0) { this.cancelBoundaryClose(); return; }
      this.lastBoundaryPoint = { x: event.clientX, y: event.clientY };
      if (insideEnvelope(this.lastBoundaryPoint, this.boundaryEnvelope())) {
        this.boundaryArmed = true;
        this.cancelBoundaryClose();
      } else if (this.boundaryArmed) this.scheduleBoundaryClose();
    }

    cancelBoundaryClose() {
      clearTimeout(this.boundaryTimer);
      this.boundaryTimer = null;
    }

    scheduleBoundaryClose(leftViewport = false) {
      if (this.boundaryTimer || !this.dialog.open) return;
      this.boundaryTimer = setTimeout(() => {
        this.boundaryTimer = null;
        if (this.dialog.open && this.boundaryArmed && (leftViewport || !insideEnvelope(this.lastBoundaryPoint, this.boundaryEnvelope()))) this.requestClose();
      }, 120);
    }

    isEmptyBackground(target) {
      return target === this.dialog || target === this.stage || target === this.petals || target === this.querySelector('[data-flower-viewport]');
    }

    requestClose() {
      this.closeUI();
      const callback = this.afterClose;
      this.afterClose = null;
      if (callback) queueMicrotask(callback);
    }

    prepareNavigation() {
      this.afterClose = null;
      this.closeUI(false, false);
    }

    lockScroll() {
      if (this.scrollLock) return;
      const properties = ['overflow', 'padding-right'];
      const body = document.body;
      this.scrollLock = { x: window.scrollX, y: window.scrollY, body: properties.map((name) => [name, body.style.getPropertyValue(name), body.style.getPropertyPriority(name)]), htmlOverflow: [document.documentElement.style.getPropertyValue('overflow'), document.documentElement.style.getPropertyPriority('overflow')] };
      const gap = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      const padding = parseFloat(getComputedStyle(body).paddingRight) || 0;
      body.style.setProperty('overflow', 'hidden');
      body.style.setProperty('padding-right', `${padding + gap}px`);
      document.documentElement.style.setProperty('overflow', 'hidden');
    }

    unlockScroll(restorePosition = true) {
      if (!this.scrollLock) return;
      const lock = this.scrollLock;
      this.scrollLock = null;
      lock.body.forEach(([name, value, priority]) => value ? document.body.style.setProperty(name, value, priority) : document.body.style.removeProperty(name));
      if (lock.htmlOverflow[0]) document.documentElement.style.setProperty('overflow', ...lock.htmlOverflow);
      else document.documentElement.style.removeProperty('overflow');
      if (restorePosition) window.scrollTo({ left: lock.x, top: lock.y, behavior: 'instant' });
    }

    closeUI(restoreFocus = true, restorePosition = true) {
      cancelAnimationFrame(this.frame);
      this.cancelHover();
      this.cancelBoundaryClose();
      this.boundaryArmed = false;
      const key = this.activeKey;
      if (key) this.hoverCooldown = Date.now() + 300;
      this.activeKey = null;
      this.internalClose = true;
      if (this.dialog?.open) this.dialog.close();
      this.internalClose = false;
      this.unlockScroll(restorePosition);
      this.cards?.get(key)?.links.forEach((link) => link.setAttribute('aria-expanded', 'false'));
      if (restoreFocus && this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
    }

    disconnectedCallback() {
      this.closeUI(false);
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.cards?.forEach(({ links }) => links.forEach((link) => ['aria-haspopup', 'aria-controls', 'aria-expanded'].forEach((name) => link.removeAttribute(name))));
    }
  }

  if (typeof module !== 'undefined' && module.exports) module.exports.AceCategoryFlower = AceCategoryFlower;
  window.customElements.define('ace-category-flower', AceCategoryFlower);
})();



